import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage, Subscription } from "../../lib/api";
import { money, shortDate } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { StatusBadge } from "../StatusBadge";

const cadences = [
  { label: "Every month", unit: "monthly", interval: 1 },
  { label: "Every 2 months", unit: "monthly", interval: 2 },
  { label: "Quarterly", unit: "monthly", interval: 3 },
  { label: "Every week", unit: "weekly", interval: 1 },
];

export function SubscriptionPanel({ disabled, mock }: { disabled: boolean; mock: boolean }) {
  const qc = useQueryClient();
  const { data: subs } = useQuery<Subscription[]>({ queryKey: ["subscriptions"], queryFn: () => api.get("/subscriptions").then((r) => r.data) });
  const [amount, setAmount] = useState(5000);
  const [cadence, setCadence] = useState(0);
  const [day, setDay] = useState(1);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const current = subs?.find((s) => (s.status === "active" || s.status === "paused") && !s.club);
  const refresh = () => ["subscriptions", "payments", "wallet"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const startEdit = () => {
    if (!current) return;
    setAmount(current.amount_pence);
    const idx = cadences.findIndex((c) => c.unit === current.interval_unit && c.interval === current.interval);
    setCadence(idx >= 0 ? idx : 0);
    setDay(current.day_of_month || 1);
    setEditing(true);
  };

  const saveChange = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/subscriptions/${current!.id}`, { amount_pence: amount, interval_unit: cadences[cadence].unit, interval: cadences[cadence].interval, day_of_month: day });
      toast.success(data.replaced ? "Plan replaced — your new cadence starts from the next collection" : "Plan amount updated");
      setEditing(false);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/subscriptions", { amount_pence: amount, interval_unit: cadences[cadence].unit, interval: cadences[cadence].interval, day_of_month: day });
      toast.success("Recurring plan created with GoCardless");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: string) => {
    try {
      if (action === "charge") {
        const { data } = await api.post(`/dev/gocardless/subscriptions/${current!.id}/charge`);
        toast(data.skipped ? "This collection was skipped" : "Subscription collection created (pending)");
      } else {
        await api.post(`/subscriptions/${current!.id}/${action}`);
        toast.success(`Plan ${action === "skip" ? "skip toggled" : action + "d"}`);
      }
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (current) {
    return (
      <div className="grid gap-10 lg:grid-cols-2" data-testid="subscription-panel">
        <div className="border border-border bg-[var(--parchment)] p-8">
          <div className="eyebrow">Your recurring plan</div>
          <div className="mt-3 font-serif text-4xl tabular-nums" data-testid="subscription-amount">{money(current.amount_pence)}</div>
          <div className="mt-1 text-sm text-neutral-600">{current.name.replace("Cellar plan: ", "").replace(/^£[\d.]+ /, "")}</div>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
            <StatusBadge status={current.status} testId="subscription-status" />
            {current.next_charge_date && <span>Next collection {shortDate(current.next_charge_date)}</span>}
            {current.skip_next && <span className="text-[var(--wine)]" data-testid="subscription-skip-flag">Next collection will be skipped</span>}
          </div>
        </div>
        <div className="space-y-3">
          <div className="eyebrow">Manage</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => (editing ? setEditing(false) : startEdit())} data-testid="subscription-change-button">{editing ? "Keep current plan" : "Change plan"}</Button>
            {current.status === "active" && <Button variant="outline" className="rounded-none" onClick={() => act("pause")} data-testid="subscription-pause-button">Pause</Button>}
            {current.status === "paused" && <Button variant="outline" className="rounded-none" onClick={() => act("resume")} data-testid="subscription-resume-button">Resume</Button>}
            {current.status === "active" && <Button variant="outline" className="rounded-none" onClick={() => act("skip")} data-testid="subscription-skip-button">{current.skip_next ? "Un-skip next" : "Skip next"}</Button>}
            <Button variant="outline" className="rounded-none text-[var(--wine)]" onClick={() => act("cancel")} data-testid="subscription-cancel-button">Cancel plan</Button>
          </div>
          {mock && current.status === "active" && (
            <div className="pt-4 text-xs text-neutral-500">
              Sandbox: <button onClick={() => act("charge")} className="underline underline-offset-4 hover:text-[var(--ink)]" data-testid="subscription-simulate-charge">simulate the next scheduled collection</button>
            </div>
          )}
          {editing && (
            <div className="mt-6 space-y-5 border border-border p-5" data-testid="subscription-edit-form">
              <div className="eyebrow">New plan</div>
              <div>
                <div className="eyebrow">Amount</div>
                <div className="mt-2 flex items-center gap-3"><span className="font-serif text-2xl">£</span><Input type="number" min={10} step={5} value={amount / 100} onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))} className="rounded-none bg-transparent text-lg tabular-nums" data-testid="subscription-edit-amount" /></div>
              </div>
              <div>
                <div className="eyebrow">Cadence</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {cadences.map((c, i) => (
                    <button key={c.label} onClick={() => setCadence(i)} className={`border py-2 text-sm transition-colors duration-200 ${cadence === i ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`subscription-edit-cadence-${c.unit}-${c.interval}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              {cadences[cadence].unit === "monthly" && (
                <div><div className="eyebrow">Day of month</div><Input type="number" min={1} max={28} value={day} onChange={(e) => setDay(Math.min(28, Math.max(1, Number(e.target.value))))} className="mt-2 w-32 rounded-none bg-transparent tabular-nums" data-testid="subscription-edit-day" /></div>
              )}
              <p className="text-xs text-neutral-500">{cadences[cadence].unit === current.interval_unit && cadences[cadence].interval === current.interval && day === (current.day_of_month || 1) ? "Amount changes apply from your next collection." : "Changing the cadence replaces your plan: the current one is cancelled and the new schedule starts from its first collection date."}</p>
              <Button onClick={saveChange} disabled={busy || amount < 1000} className="w-full rounded-none py-5 hover-lift" data-testid="subscription-edit-save">{busy ? "Updating…" : `Switch to ${money(amount)} ${cadences[cadence].label.toLowerCase()}`}</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-10 lg:grid-cols-2" data-testid="subscription-panel">
      <div>
        <div className="eyebrow">Recurring plan</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Build your account balance automatically</h2>
        <p className="mt-3 text-sm text-neutral-600">Choose an amount and cadence. Each collection is credited to your account when confirmed. Pause, skip or cancel at any time.</p>
      </div>
      <div className="space-y-6">
        <div>
          <div className="eyebrow">Amount</div>
          <div className="mt-2 flex items-center gap-3"><span className="font-serif text-2xl">£</span><Input type="number" min={10} step={5} value={amount / 100} onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))} className="rounded-none bg-transparent text-lg tabular-nums" data-testid="subscription-amount-input" /></div>
        </div>
        <div>
          <div className="eyebrow">Cadence</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {cadences.map((c, i) => (
              <button key={c.label} onClick={() => setCadence(i)} className={`border py-3 text-sm transition-colors duration-200 ${cadence === i ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`cadence-${c.unit}-${c.interval}`}>{c.label}</button>
            ))}
          </div>
        </div>
        {cadences[cadence].unit === "monthly" && (
          <div>
            <div className="eyebrow">Day of month</div>
            <Input type="number" min={1} max={28} value={day} onChange={(e) => setDay(Math.min(28, Math.max(1, Number(e.target.value))))} className="mt-2 w-32 rounded-none bg-transparent tabular-nums" data-testid="subscription-day-input" />
          </div>
        )}
        <Button onClick={create} disabled={disabled || busy || amount < 1000} className="w-full rounded-none py-6 text-base hover-lift" data-testid="subscription-create-button">{disabled ? "Connect your bank first" : busy ? "Creating…" : `Start plan · ${money(amount)} ${cadences[cadence].label.toLowerCase()}`}</Button>
      </div>
    </div>
  );
}
